import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { MyShelf, myShelfMapFields, sfMyShelf } from 'app/backend/salesforce-maps/MyShelf'
import { Resource, sfResource } from 'app/services/sfAuth/sfData/sfResources'
import { mapSFToForm } from 'app/services/sfAuth/sfDataService'
import { getTranslationIdFromSalesforceApiValue } from 'app/views/common/translationsCommon'

export interface FilterValues {
  Centre_Priority: string[]
  Topic: string[]
  Location: string[]
  Type: string[]
  Access: string[]
  Language: string[]
}

export interface FilterOption {
  key: keyof FilterParams
  isChecked: boolean
  label: string
}

export interface FilterCardProps {
  filters: FilterOption[]
  label: string
  onFilterChange?: (selectedFilters: FilterOption[]) => void
  className?: string
}

export interface FilterParams {
  topic?: string[]
  location?: string[]
  type?: string[]
  grant?: string[]
  language?: string[]
  access?: string[]
  q?: string
}

export const resourceFieldsMapSF = {
  direction: 'in',
  Number__c: 'number',
  Name: 'systemName',
  Centre_Priority__c: 'centrePriority',
  Topic__c: 'topic',
  Location__c: 'location',
  Type__c: 'type',
  Description__c: 'description',
  Id: 'id',
  Year__c: 'year',
  Resource_Name__c: 'name',
  Language_of_Entry__c: 'entryLanguage',
  Language_of_Resource__c: 'language',
  Access__c: 'access',
  Resource_URL__c: 'url',
  URL_submission__c: 'urlSubmission',
  Global_ID__c: 'globalId',
  Organization_Name__c: 'organization',
  Contact__c: 'contact',
  CreatedById: 'createdBy',
  CreatedDate: 'createdDate',
  IsDeleted: 'isDeleted',
  LastModifiedById: 'lastModifiedBy',
  LastModifiedDate: 'lastModifiedDate',
  Last_Modified_User_Data__c: 'lastModifiedUserData',
  Last_Modified_By_User_Date__c: 'lastModifiedByUserDate',
  Resource_name_submission__c: 'resourceNameSubmission',
  LastReferencedDate: 'lastReferencedDate',
  LastViewedDate: 'lastViewedDate',
  OwnerId: 'ownerId',
  Resource__c: 'resource',
  SystemModstamp: 'systemModstamp',
  Current_number_of_Saves__c: 'currentNumberOfSaves',
  Date_published__c: 'datePublished',
  Stage__c: 'stage',
  CONTROL_Publish_to_Website_Status__c: 'controlPublishToWebsiteStatus',
  Sources__r: {
    key: 'sources',
    in: (opp: sfResource) => {
      // console.log({ opp })
      return opp.Sources__r?.records.map((obj) => ({
        id: obj.Id,
        name: obj?.Display_Name__c,
        organizationId: obj?.Organization__r?.Id,
        organizationName: obj?.Organization__r?.Name,
        contactId: obj?.Contact__r?.Id,
        contactName: obj?.Contact__r?.Name
      }))
    }
  }
}

const translateValue = (value: string): string => {
  return resourceFiltersTranslations[value] || value
}

const translateArrayValues = (values: string[]): string[] => {
  return values.map(value => translateValue(value))
}

export const parseResourceSFObject = (obj: sfResource): Resource => {
  return {
    ...mapSFToForm(resourceFieldsMapSF, obj),
    type: translateValue(obj.Type__c),
    centrePriority: [...translateArrayValues(splitAndTrim(obj.Topic__c)), ...translateArrayValues(splitAndTrim(obj.Centre_Priority__c))],
    topic: [...translateArrayValues(splitAndTrim(obj.Topic__c)), ...translateArrayValues(splitAndTrim(obj.Centre_Priority__c))],
    access: translateValue(obj.Access__c),
    language: translateArrayValues(splitAndTrim(obj.Language_of_Resource__c)),
    location: translateArrayValues(splitAndTrim(obj.Location__c))
  }
}

export const parseUrlParam = (param?: string): string[] => {
  if (!param) return []
  return param.split(',').filter(Boolean)
}

export const updateUrlWithFilters = (
  filterKey: keyof FilterParams | 'centrePriority',
  values: string[] | string,
  currentFilterParams: FilterParams
): void => {
  // Update our internal state of filter params
  if (Array.isArray(values)) {
    (currentFilterParams[filterKey] as string[]) = values
  } else {
    (currentFilterParams[filterKey] as string[]) = values ? [values] : []
  }

  // Construct new search params from our complete state
  const searchParams = new URLSearchParams()

  // Add all non-empty params to the URL
  Object.entries(currentFilterParams).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      searchParams.set(key, value.join(','))
    } else if (!Array.isArray(value) && value) {
      searchParams.set(key, value)
    }
  })

  const newUrl = `${window.location.pathname}?${searchParams.toString()}`

  // Use replaceState to update URL without navigation
  window.history.replaceState(
    { ...(window.history.state || {}), filterUpdate: true },
    '',
    newUrl
  )
}

export const createFilterOptions = (values: string[] | undefined, selectedValues?: string[]): FilterOption[] => {
  const selected = selectedValues ?? []
  if (values === undefined) {
    return []
  }
  return values.map(value => ({
    isChecked: selected.includes(value),
    label: i18n._(t`${getTranslationIdFromSalesforceApiValue(value)}`),
    key: value as keyof FilterParams
  })).sort((a, b) => a.label.localeCompare(b.label))
}

export const matchesFilters = (params: FilterParams, resource: Resource): boolean => {
  // Check Topic filter
  const topics = params.topic ?? []
  if ((topics.length > 0) && !topics.some(topic =>
    resource.topic?.includes(topic)
  )) {
    return false
  }

  const centrePriorities = params.centrePriority ?? []
  if ((centrePriorities.length > 0) && !centrePriorities.some(priority =>
    resource.centrePriority?.includes(priority)
  )) {
    return false
  }

  // Check Location filter
  const locations = params.location ?? []
  if ((locations.length > 0) && !locations.some(location =>
    resource.location?.includes(location)
  )) {
    return false
  }

  // Check Type filter
  const types = params.type ?? []
  if ((types.length > 0) && !types.some(type =>
    resource.type === type
  )) {
    return false
  }

  // Check Grant Project (Centre_Priority) filter
  const grants = params.grant ?? []
  if ((grants.length > 0) && !grants.some(grant =>
    resource.centrePriority?.includes(grant)
  )) {
    return false
  }

  // Check Access filter
  const access = params.access ?? []
  if ((access.length > 0) && !access.some(access =>
    resource.access?.includes(access)
  )) {
    return false
  }

  // Check Grant language filter
  const language = params.language ?? []
  if ((language.length > 0) && !language.some(language =>
    resource.language?.includes(language)
  )) {
    return false
  }

  return true
}

export const splitAndTrim = (input: string): string[] => {
  return input
    ? input.split(';').map(item => item.trim()).filter(item => item !== '')
    : []
}

export const getPropertyValues = (
  propertyName: keyof Resource,
  resourceList: Resource[]
): string[] => {
  const values = new Set<string>()

  resourceList.forEach((resource) => {
    const value = resource[propertyName]

    if (value) {
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v) {
            value.forEach(v => splitAndTrim(v).forEach(item => values.add(item)))
          }
        })
      } else {
        splitAndTrim(value).forEach(item => values.add(item))
      }
    }
  })

  return Array.from(values).sort()
}

export const getResourceFilterValues = (describeResult): FilterValues => {
  const getActivePicklistTranslations = (object): string[] => {
    return object?.picklistValues.filter(obj => obj.active).map(obj => {
      if (resourceFiltersTranslations[obj.value] === undefined) {
        return obj.value
      }
    }).filter(value => value !== undefined)
  }
  return {
    // Centre_Priority: getActivePicklistTranslations(describeResult.fieldsMap.Centre_Priority__c),
    Topic: [...getActivePicklistTranslations(describeResult.fieldsMap.Topic__c), ...getActivePicklistTranslations(describeResult.fieldsMap.Centre_Priority__c)],
    Location: getActivePicklistTranslations(describeResult.fieldsMap.Location__c),
    Type: getActivePicklistTranslations(describeResult.fieldsMap.Type__c),
    Access: getActivePicklistTranslations(describeResult.fieldsMap.Access__c),
    Language: getActivePicklistTranslations(describeResult.fieldsMap.Language_of_Resource__c)
  }
}

export const groupResourcesByLanguage = (resources: Resource[], userLanguage: string): Resource[] => {
  const map: Record<string, Record<string, any>> = {}
  const language = userLanguage.toUpperCase()
  resources.forEach(item => {
    if (!map[item.globalId]) {
      map[item.globalId] = {}
    }
    map[item.globalId][item.entryLanguage || 'EN'] = item
  })

  return Object.keys(map)
    .map(objectId =>
      map[objectId][language] ||
      map[objectId].EN ||
      map[objectId].FR ||
      map[objectId].UI
    )
    .filter(item => item !== undefined)
}

export const resourceFiltersTranslations = {
  'Membres et locataires': 'Members & Tenants',
  Développement: 'Development',
  Gouvernance: 'Governance',
  Immeubles: 'Property',
  'Vivre ensemble': 'Community',
  'Documents et outils': 'Documents & Tools',
  'Activités et formations': 'Events & Training',
  'Financement et services': 'Funding & Services',
  Croissance: 'Growth',
  Environnement: 'Environment',
  Autochtone: 'Indigenous',
  Gratuit: 'Free',
  'Avec adhésion': 'Membership',
  Payant: 'Pay-per-use',
  'Colombie-Britannique': 'British Columbia',
  'Nouveau-Brunswick': 'Newfoundland and Labrador',
  'Terre-Neuve-et-Labrador': 'Newfoundland and Labrador',
  'Territoires du Nord-Ouest': 'Northwest Territories',
  'Nouvelle-Écosse': 'Nova Scotia',
  'Île-du-Prince-Édouard': 'Prince Edward Island'
}
